import React, { useState } from "react";
import { Trans } from "react-i18next";
import CountUp from "react-countup";
import HeaderTS from "../global/headerTS";
import Footer2 from "../global/footerv2";
import SectionTitle from "../section-title";
import Ctav3 from "../ctav3";
import erpData from "../../data/erp-system.json";

const ErpSystem = ({ header }) => {
  const {
    erpHero,
    erpFlow,
    erpFeatures,
    erpModules,
    erpScreens,
    erpWorkflow,
    erpPricing,
  } = erpData;
  const [billing, setBilling] = useState("monthly");
  const [activeScreen, setActiveScreen] = useState(erpScreens.screens[0].id);
  const screen =
    erpScreens.screens.find((s) => s.id === activeScreen) ||
    erpScreens.screens[0];
  const isYearly = billing === "yearly";

  return (
    <>
      <HeaderTS header={header} />

      {/* Hero Section */}
      <section id="hero" className="hero3 hero-padding bg-one">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 hero3__content">
              <p className="badge-text">
                <Trans i18nKey="erpSystem.hero.subtitle">
                  {erpHero.subtitle}
                </Trans>
              </p>
              <h1 className="display-4 mb-4 hero3__title">
                <Trans i18nKey="erpSystem.hero.title">{erpHero.title}</Trans>
              </h1>
              <p className="fs-5 mb-4">
                <Trans i18nKey="erpSystem.hero.description">
                  {erpHero.description}
                </Trans>
              </p>
              <p className="tcm-hero__price">
                <i className="icofont-tag"></i>
                <Trans i18nKey="erpSystem.hero.price">{erpHero.price}</Trans>
              </p>

              <div className="d-flex flex-wrap align-items-center justify-content-center justify-content-sm-start gap-3">
                <a
                  rel="noreferrer"
                  href={`tel:${erpHero.contactPhone}`}
                  className="btn__primary2 btn-md smooth"
                >
                  <span>
                    <i className="icofont-phone icofont-1x"></i>
                    &nbsp;&nbsp;&nbsp;
                    <Trans i18nKey="erpSystem.hero.phone">Phone</Trans>
                  </span>
                </a>

                <a
                  rel="noreferrer"
                  href={`https://wa.me/${erpHero.contactWhatsApp.replace(
                    /[\s+]/g,
                    ""
                  )}`}
                  className="btn__primary2 btn-md smooth"
                  target="_blank"
                >
                  <span>
                    <i className="icofont-brand-whatsapp icofont-1x"></i>
                    &nbsp;&nbsp;&nbsp;
                    <Trans i18nKey="erpSystem.hero.whatsapp">WhatsApp</Trans>
                  </span>
                </a>

                <a
                  rel="noreferrer"
                  href={`mailto:${erpHero.contactEmail}`}
                  className="btn__primary2 btn-md smooth"
                >
                  <span>
                    <i className="icofont-mail icofont-1x"></i>
                    &nbsp;&nbsp;&nbsp;{erpHero.contactEmail}
                  </span>
                </a>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="hero3__image erp-hero__circle">
                <img className="img-fluid" src={erpHero.image} alt={erpHero.subtitle} />

                {erpHero.orbit.map((item) => (
                  <div
                    key={item.id}
                    className={`hero3__image__icon ${item.position}`}
                  >
                    <i className={item.icon}></i>
                    <p>
                      <Trans i18nKey={`erpSystem.hero.orbit${item.id}`}>
                        {item.title}
                      </Trans>
                    </p>
                  </div>
                ))}

                <div className="erp-hero__badge">
                  <h5>
                    <CountUp
                      end={erpHero.badge.counterNumber}
                      enableScrollSpy="true"
                    />
                  </h5>
                  <span>
                    <Trans i18nKey="erpSystem.hero.badge">
                      {erpHero.badge.title}
                    </Trans>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section id="features" className="service section-padding erp-features">
        <div className="container">
          <SectionTitle
            subtitle={
              <Trans i18nKey="erpSystem.features.subtitle">
                {erpFeatures.subtitle}
              </Trans>
            }
            title={
              <Trans i18nKey="erpSystem.features.title">
                {erpFeatures.title}
              </Trans>
            }
            direction="center"
          />
          <div className="row pt-4 gy-5">
            {erpFeatures.features.map((feature, i) => (
              <div key={feature.id} className="col-md-6 col-lg-4">
                <div className="service__card translateEffect1 h-100">
                  <div className="service__card__icon">
                    <i className={feature.icon}></i>
                  </div>
                  <div className="service__card__content">
                    <h4>
                      <Trans
                        i18nKey={`erpSystem.features.feature${i + 1}.title`}
                      >
                        {feature.title}
                      </Trans>
                      {feature.tag && (
                        <span className="erp-tag">
                          <Trans i18nKey="erpSystem.features.plusTag">
                            {feature.tag}
                          </Trans>
                        </span>
                      )}
                    </h4>
                    <p>
                      <Trans
                        i18nKey={`erpSystem.features.feature${
                          i + 1
                        }.description`}
                      >
                        {feature.description}
                      </Trans>
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrated Flow Section */}
      <section id="flow" className="section-padding erp-flow bg-one">
        <div className="container">
          <SectionTitle
            subtitle={
              <Trans i18nKey="erpSystem.flow.subtitle">{erpFlow.subtitle}</Trans>
            }
            title={
              <Trans i18nKey="erpSystem.flow.title">{erpFlow.title}</Trans>
            }
            direction="center"
          />
          <ol className="erp-flow__ribbon">
            {erpFlow.steps.map((step, i) => (
              <li
                key={step.id}
                // earlier panels paint over the notch of the next one
                style={{ zIndex: erpFlow.steps.length - i }}
                className={`erp-flow__ribbon--step${
                  i === erpFlow.steps.length - 1 ? " is-final" : ""
                }`}
              >
                <i className={step.icon}></i>
                <h5>
                  <Trans i18nKey={`erpSystem.flow.step${i + 1}`}>
                    {step.title}
                  </Trans>
                </h5>
                <span className="erp-flow__ribbon--doc">
                  <Trans i18nKey={`erpSystem.flow.doc${i + 1}`}>
                    {step.doc}
                  </Trans>
                </span>
              </li>
            ))}
          </ol>
          <div className="erp-flow__note">
            <i className="icofont-info-circle"></i>
            <p>
              <Trans i18nKey="erpSystem.flow.note">{erpFlow.note}</Trans>
            </p>
          </div>
        </div>
      </section>

      {/* Modules Section */}
      <section id="modules" className="section-padding erp-modules">
        <div className="container">
          <SectionTitle
            subtitle={
              <Trans i18nKey="erpSystem.modules.subtitle">
                {erpModules.subtitle}
              </Trans>
            }
            title={
              <Trans i18nKey="erpSystem.modules.title">
                {erpModules.title}
              </Trans>
            }
            direction="center"
          />
          <div className="row pt-4 g-4 justify-content-center">
            {erpModules.modules.map((module, i) => (
              <div key={module.id} className="col-6 col-md-4 col-lg-3">
                <div className="erp-module translateEffect2 h-100">
                  <i className={module.icon}></i>
                  <h5>
                    <Trans i18nKey={`erpSystem.modules.module${i + 1}.title`}>
                      {module.title}
                    </Trans>
                  </h5>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshots Section */}
      <section id="screens" className="section-padding erp-screens bg-one">
        <div className="container">
          <SectionTitle
            subtitle={
              <Trans i18nKey="erpSystem.screens.subtitle">
                {erpScreens.subtitle}
              </Trans>
            }
            title={
              <Trans i18nKey="erpSystem.screens.title">
                {erpScreens.title}
              </Trans>
            }
            direction="center"
          />
          <div className="erp-screens__tabs" role="tablist">
            {erpScreens.screens.map((s) => (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={s.id === screen.id}
                className={`erp-screens__tab ${
                  s.id === screen.id ? "is-active" : ""
                }`}
                onClick={() => setActiveScreen(s.id)}
              >
                <i className={s.icon}></i>
                <Trans i18nKey={`erpSystem.screens.screen${s.id}.title`}>
                  {s.title}
                </Trans>
              </button>
            ))}
          </div>
          <div className="erp-screens__frame" role="tabpanel">
            <div className="erp-screens__frame--bar">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <a href={screen.image} target="_blank" rel="noreferrer">
              <img
                key={screen.id}
                className="img-fluid"
                src={screen.image}
                alt={screen.title}
                width="1440"
                height="900"
              />
            </a>
          </div>
          <div className="erp-screens__caption">
            <p>
              <Trans i18nKey={`erpSystem.screens.screen${screen.id}.description`}>
                {screen.description}
              </Trans>
            </p>
            <span>
              <i className="icofont-expand"></i>{" "}
              <Trans i18nKey="erpSystem.screens.enlarge">
                {erpScreens.enlarge}
              </Trans>
            </span>
          </div>
        </div>
      </section>

      {/* Inventory Workflow Section */}
      <section
        id="workflow"
        className="overview section-padding dark-bg erp-workflow-section"
      >
        <div className="container">
          <SectionTitle
            subtitle={
              <Trans i18nKey="erpSystem.workflow.subtitle">
                {erpWorkflow.subtitle}
              </Trans>
            }
            title={
              <Trans i18nKey="erpSystem.workflow.title">
                {erpWorkflow.title}
              </Trans>
            }
            direction="center"
          />
          <div className="row erp-workflow">
            {erpWorkflow.steps.map((step) => (
              <div
                key={step.id}
                className="col-lg col-md-6 mb-4 mb-lg-0 erp-workflow__col"
              >
                <div className="overview__content translateEffect2 h-100">
                  <div className="erp-workflow__icon">
                    <i className={step.icon}></i>
                  </div>
                  <div className="overview__content--title">
                    <h2>{step.number}</h2>
                    <h3>
                      <Trans i18nKey={`erpSystem.workflow.step${step.id}.title`}>
                        {step.title}
                      </Trans>
                    </h3>
                    <p>
                      <Trans
                        i18nKey={`erpSystem.workflow.step${step.id}.description`}
                      >
                        {step.description}
                      </Trans>
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-5">
            <p className="fs-5">
              <Trans i18nKey="erpSystem.workflow.note">{erpWorkflow.note}</Trans>
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing section-padding erp-pricing bg-one">
        <div className="container">
          <SectionTitle
            subtitle={
              <Trans i18nKey="erpSystem.pricing.subtitle">
                {erpPricing.subtitle}
              </Trans>
            }
            title={
              <Trans i18nKey="erpSystem.pricing.title">
                {erpPricing.title}
              </Trans>
            }
            direction="center"
          />

          <div className="erp-billing" role="group">
            <button
              type="button"
              className={`erp-billing__btn ${!isYearly ? "is-active" : ""}`}
              aria-pressed={!isYearly}
              onClick={() => setBilling("monthly")}
            >
              <Trans i18nKey="erpSystem.pricing.monthly">
                {erpPricing.monthly}
              </Trans>
            </button>
            <button
              type="button"
              className={`erp-billing__btn ${isYearly ? "is-active" : ""}`}
              aria-pressed={isYearly}
              onClick={() => setBilling("yearly")}
            >
              <Trans i18nKey="erpSystem.pricing.yearly">
                {erpPricing.yearly}
              </Trans>
            </button>
          </div>
          {isYearly && (
            <p className="erp-billing__save">
              <i className="icofont-tag"></i>{" "}
              <Trans i18nKey="erpSystem.pricing.save">{erpPricing.save}</Trans>
            </p>
          )}

          <div className="row align-items-stretch justify-content-center pt-4">
            {erpPricing.plans.map((plan, i) => (
              <div
                key={plan.id}
                className="col-md-6 col-lg-5 pb-4 mb-5 mb-lg-0"
              >
                <div className="pricing__item translateEffect1 h-100">
                  <span className="pricing__item__tag">
                    <Trans i18nKey={`erpSystem.pricing.plan${i + 1}.tag`}>
                      {plan.tag}
                    </Trans>
                  </span>
                  <div className="pricing__item__icon">
                    <i className={plan.icon}></i>
                  </div>
                  <h3 className="fs-1 mt-2">
                    {erpPricing.currency}
                    {isYearly ? plan.priceYearly : plan.priceMonthly}
                  </h3>
                  <p className="pb-3">
                    {isYearly ? (
                      <Trans i18nKey="erpSystem.pricing.perYear">
                        {erpPricing.perYear}
                      </Trans>
                    ) : (
                      <Trans i18nKey="erpSystem.pricing.perMonth">
                        {erpPricing.perMonth}
                      </Trans>
                    )}
                  </p>
                  <hr />
                  <div className="pricing__item__list">
                    <ul className="pt-3">
                      {plan.features.map((detail, j) => (
                        <li key={j}>
                          <div className="pricing__item__list--ico">
                            <i className={plan.checkIcon}></i>
                          </div>
                          <p>
                            <Trans
                              i18nKey={`erpSystem.pricing.plan${plan.id}.${
                                j + 1
                              }`}
                            >
                              {detail.item}
                            </Trans>
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <a
                    href={`mailto:${erpHero.contactEmail}`}
                    className="btn__primary"
                  >
                    <span>
                      <Trans i18nKey="erpSystem.pricing.button">
                        {erpPricing.button}
                      </Trans>{" "}
                      <i className="icofont-arrow-right"></i>
                    </span>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <Ctav3 className="erp-cta" />

      <Footer2 />
    </>
  );
};

export default ErpSystem;
